import React from 'react';
import i18next from 'i18next';
import { useFormik, FormikProps } from 'formik';
import OtoFormGroup from '../common/OtoFormGroup';
import { Col, Label, Row } from 'reactstrap';

import {
  CompanyContactPosition,
  ICompanyContact,
} from 'types/company';
import APIService from 'services/api';
import { toast } from 'react-toastify';
import InputWithOptions from '../common/InputWithOptions';
import { LOG_BAG } from 'utils/log';

export interface AddContactValues {
  company_id: number;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  position: CompanyContactPosition;
}

interface IProps {
  companyId: number;
  handleSubmit: (contact: ICompanyContact) => void;
  children: (params: {
    form: React.ReactNode;
    triggerSubmit: () => void;
    isLoading: boolean;
    isDisabled: boolean;
  }) => JSX.Element;
}

const positionOptions = [
  {
    get label() { return i18next.t('add-company-contact-form.position-owner'); },
    value: 'owner',
  },
  {
    get label() { return i18next.t('add-company-contact-form.position-manager'); },
    value: 'manager',
  },
];

export const AddCompanyContactForm = (props: IProps) => {
  const { companyId, children, handleSubmit } = props;

  const formik: FormikProps<AddContactValues> = useFormik<AddContactValues>({
    initialValues: {
      company_id: companyId,
      first_name: '',
      last_name: '',
      email: '',
      phone: '+48',
      position: 'owner',
    },
    onSubmit: (values) => {
      const contact: ICompanyContact = {
        ...values,
      };
      const url = `/companies/${values.company_id}/contacts`;
      return APIService.post(url, contact)
        .then((savedContact) => {
          toast.success(i18next.t('add-company-contact-form.contact-saved'));
          return handleSubmit(savedContact as ICompanyContact);
        })
        .catch((error) => {
          toast.error(i18next.t('add-company-contact-form.contact-save-error'));
          LOG_BAG.logError('Add company contact error', error);
        });
    },
  });

  const form = (
    <form>
      <Row>
        <Col xs="12" md="auto">
          <OtoFormGroup
            label={i18next.t('add-company-contact-form.first-name')}
            name="first_name"
            onChange={formik.handleChange}
            value={formik.values.first_name}
          />
        </Col>
        <Col xs="12" md="auto">
          <OtoFormGroup
            label={i18next.t('add-company-contact-form.last-name')}
            name="last_name"
            onChange={formik.handleChange}
            value={formik.values.last_name}
          />
        </Col>
        <Col xs="12" md="auto">
          <OtoFormGroup
            label={i18next.t('add-company-contact-form.phone')}
            name="phone"
            type="tel"
            onChange={formik.handleChange}
            value={formik.values.phone}
          />
        </Col>
        <Col xs="12" md="auto">
          <OtoFormGroup
            label={i18next.t('add-company-contact-form.email')}
            name="email"
            type="email"
            onChange={formik.handleChange}
            value={formik.values.email}
          />
        </Col>
        <Col xs="12" md="auto">
          <Label for="position">{i18next.t('add-company-contact-form.position')}</Label>
          <InputWithOptions
            id="position"
            name="position"
            onChange={formik.handleChange}
            value={formik.values.position}
            options={positionOptions}
          />
        </Col>
      </Row>
    </form>
  );
  return children({
    form,
    triggerSubmit: formik.submitForm,
    isLoading: formik.isSubmitting,
    isDisabled: false,
  });
};
